import { Link } from "@tanstack/react-router"
import {
  CreditCard,
  FileText,
  Grid3x3,
  LayoutDashboard,
  Send,
} from "lucide-react"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@holigral/shared/ui/components/ui/sidebar"
import { useIsRouteActive } from "@holigral/shared/routing"
import { useT } from "@/i18n"
import { useGetCurrentTenantQuery } from "@/tenant"

type Props = {
  readonly tenantSlug: string
}

/**
 * Per-tenant navigation (product.md §10.5). Team lives under Settings for now;
 * Billing is Owner-only (billingAdmin). Settings stays in the sidebar footer.
 */
export default function TenantNav({ tenantSlug }: Props) {
  const { t } = useT("common")
  const { data: tenant } = useGetCurrentTenantQuery()
  // Active state is a hook, so it cannot be resolved inside the map below.
  const isDashboardActive = useIsRouteActive({
    to: "/t/$tenantSlug/dashboard",
    fuzzy: true,
  })
  const isAssignmentsActive = useIsRouteActive({
    to: "/t/$tenantSlug/assignments",
    fuzzy: true,
  })
  const isBriefsActive = useIsRouteActive({
    to: "/t/$tenantSlug/briefs",
    fuzzy: true,
  })
  const isEvaluationsActive = useIsRouteActive({
    to: "/t/$tenantSlug/evaluations",
    fuzzy: true,
  })
  const isBillingActive = useIsRouteActive({
    to: "/t/$tenantSlug/billing",
    fuzzy: true,
  })

  const items = [
    {
      to: "/t/$tenantSlug/dashboard",
      icon: <LayoutDashboard />,
      label: t("nav.dashboard"),
      isActive: isDashboardActive,
    },
    {
      to: "/t/$tenantSlug/assignments",
      icon: <Send />,
      label: t("nav.assignments"),
      isActive: isAssignmentsActive,
    },
    {
      to: "/t/$tenantSlug/briefs",
      icon: <FileText />,
      label: t("nav.briefs"),
      isActive: isBriefsActive,
    },
    {
      to: "/t/$tenantSlug/evaluations",
      icon: <Grid3x3 />,
      label: t("nav.evaluations"),
      isActive: isEvaluationsActive,
    },
  ] as const

  return (
    <SidebarMenu>
      {items.map(({ to, icon, label, isActive }) => (
        <SidebarMenuItem key={to}>
          <SidebarMenuButton
            isActive={isActive}
            render={
              <Link to={to} params={{ tenantSlug }}>
                {icon}
                {label}
              </Link>
            }
          />
        </SidebarMenuItem>
      ))}
      {tenant?.permissions.billingAdmin && (
        <SidebarMenuItem>
          <SidebarMenuButton
            isActive={isBillingActive}
            render={
              <Link to="/t/$tenantSlug/billing" params={{ tenantSlug }}>
                <CreditCard />
                {t("nav.billing")}
              </Link>
            }
          />
        </SidebarMenuItem>
      )}
    </SidebarMenu>
  )
}
